import { ChangeDetectionStrategy, Component, computed, contentChild, inject, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronDown } from '@ng-icons/lucide';
import { BrnSelect, BrnSelectTrigger } from '@spartan-ng/brain/select';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { hlm } from '@spartan-ng/helm/utils';
import { cva } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const selectTriggerVariants = cva(
  `component-medium-medium border-stroke-neutral-medium outline-feedback-informative-main active:outline-stroke-neutral-dark [&>ng-icon]:text-muted-foreground hover:bg-components-background-neutral-lighter hover:text-components-content-neutral-primary py-xs px-sm flex w-full min-w-[5rem] items-center justify-between gap-2 rounded-md border bg-white text-sm whitespace-nowrap outline-offset-2 transition-[color,box-shadow] focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-[44px] data-[size=sm]:h-8 dark:bg-white [&>ng-icon]:pointer-events-none [&>ng-icon]:size-4 [&>ng-icon]:shrink-0`,
  {
    variants: {
      error: {
        auto: '[&.ng-invalid.ng-touched]:text-danger [&.ng-invalid.ng-touched]:border-danger [&.ng-invalid.ng-touched]:focus-visible:ring-danger/20 dark:[&.ng-invalid.ng-touched]:focus-visible:ring-danger/40',
        true: 'text-danger border-danger focus-visible:ring-danger/20 dark:focus-visible:ring-danger/40',
      },
    },
    defaultVariants: {
      error: 'auto',
    },
  },
);

@Component({
  selector: 'hlm-select-trigger',
  imports: [BrnSelectTrigger, NgIcon, HlmIcon],
  providers: [provideIcons({ lucideChevronDown })],
  template: `
    <button #button [class]="_computedClass()" [attr.data-size]="size()" hlmInput brnSelectTrigger type="button">
      <ng-content select="ng-icon" />
      <ng-content />
      <ng-icon class="ml-2 flex-none" hlm size="sm" name="lucideChevronDown" />
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HlmSelectTrigger {
  protected readonly _icon = contentChild(HlmIcon);

  protected readonly _brnSelect = inject(BrnSelect, { optional: true });

  public readonly userClass = input<ClassValue>('', { alias: 'class' });

  public readonly size = input<'default' | 'sm'>('default');

  protected readonly _computedClass = computed(() =>
    hlm(selectTriggerVariants({ error: this._brnSelect?.errorState() }), this.userClass()),
  );
}
